import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SearchInput } from "component/TableSearchInput";
import {
  ArrowUpDown,
  CheckCircleIcon,
  Notification as NotificationIcon,
  OrangeLock,
  Settings,
} from "asset/icons";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "component/ui/Dropdown";
import useGetRequest from "hooks/useGetRequest";
import sortListByDate from "utilities/sortListByDate";
import { useDispatch, useSelector } from "react-redux";
import NotificationDropdown from "./NotificationDropdown";
import capitalizeWords from "utilities/capitalizeWords";
import Avatar from "component/avatar";
import { cn } from "lib/utils";
import { Add, HamburgerMenu, Lock, LogoutCurve, Setting2 } from "iconsax-reactjs";
import { Drawer, DrawerContent, DrawerTrigger } from "../ui/Drawer";
import NewSideNav from "./NewSideNav";
import axios from "axios";
import { LOGOUT_USER } from "../../store/action";
import NotificationBar from "./NotificationBar";
import AdditionalInfoBanner from "./AdditionalInfoBanner";
import AccountOpeningBanner from "./AccountOpeningBanner";
import CompleteAccountOpeningModal from "../wallet/CompleteAccountOpeningModal";
import WaitingToConfirmTransferModal from "../wallet/WaitingToConfirmTransferModal";
import AccountOpeningSuccessModal from "../wallet/AccountOpeningSuccessModal";
import useOpeningFeeSocket from "hooks/useOpeningFeeSocket";
import useOpeningFees from "hooks/useOpeningFees";
import { useNavigate } from "react-router-dom";
import useUserDetails from "hooks/useUserDetails";
import { useQuery } from "react-query";
import { fetchBusinessData } from "api/fetchers";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { currencyToDisplay } from "../flex/utils";

const getBusinessDefaultCurrency = (business) => {
  const countryCode = String(
    business?.businessCountry?.code || business?.countryCode || ""
  ).toUpperCase();
  const countryName = String(
    business?.businessCountry?.name || business?.country || ""
  ).toLowerCase();

  return countryCode === "NG" || countryName === "nigeria" ? "NGN" : "USD";
};

const getWalletCurrencies = (response) => {
  const wallets =
    (Array.isArray(response?.data?.data) && response.data.data) ||
    (Array.isArray(response?.data) && response.data) ||
    [];

  return {
    currencies: wallets
      .map((wallet) => String(wallet?.currency || wallet?.code || "").toUpperCase())
      .filter(Boolean),
    wallets,
  };
};

const EMPTY_ARRAY = [];

const NewDashboardLayout = ({ children, direction = "left" }) => {
  const user = useSelector((state) => state?.users);
  const isLogin = useSelector((state) => state?.isLogin);
  const business = useSelector((state) => state?.business);
  const currency = useSelector((state) => state?.currency);
  const businessList = useSelector((state) => state?.businessList);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  const [switchInProgress, setSwitchInProgress] = useState(false); // Track switch progress
  const [selectedBizId, setSelectedBizId] = useState();
  const [activeOpeningModal, setActiveOpeningModal] = useState(null); // 'payment' | 'waiting' | 'delay' | 'success' | null
  const [isOpeningFeeConfirmed, setIsOpeningFeeConfirmed] = useState(false);
  const [openingFeeBannerStatus, setOpeningFeeBannerStatus] = useState("approved"); // 'approved' | 'pending' | 'failed'
  const [fetchNotifications, _notifications, notificationsLoading, , , setNotificationsLoading] =
    useGetRequest();
  const {
    currentBusinessAccount,
    isLoadingCurrentBusinessAccount,
    refetchCurrentBusinessAccount,
    userWallets,
    userWalletsAccounts,
  } = useUserDetails();
  const { openingFees, isLoading: isLoadingOpeningFees } = useOpeningFees();

  const { data: fetchedBusinesses, isFetching: isFetchingBusinesses } = useQuery({
    // Business membership is user-specific. Never reuse another user's list.
    queryKey: ['businessList', user?._id],
    queryFn: fetchBusinessData,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      const payload = data?.data || data;
      if (Array.isArray(payload)) {
        dispatch({ type: "BUSINESSES_LIST_RES", payload });
      }
    },
    enabled: Boolean(isLogin && user?._id),
  });

  const fetchedBusinessList =
    fetchedBusinesses?.data?.data || fetchedBusinesses?.data || fetchedBusinesses;
  const allBusinesses = Array.isArray(fetchedBusinessList)
    ? fetchedBusinessList
    : Array.isArray(businessList)
      ? businessList
      : EMPTY_ARRAY;

  const businessesData = useMemo(() => {
    if (!allBusinesses.length) {
      return { businesses: EMPTY_ARRAY, selected: undefined };
    }
    const activeBusinesses = allBusinesses.filter(({ isDeleted }) => !isDeleted);

    return {
      businesses: activeBusinesses.filter(({ isCurrent }) => !isCurrent),
      selected: activeBusinesses.find(({ isCurrent }) => isCurrent),
    };
  }, [allBusinesses]);

  useEffect(() => {
    if (!selectedBizId) return;

    let cancelled = false;
    const switchBusiness = async () => {
      const nextBusiness = (allBusinesses || []).find(({ _id }) => _id === selectedBizId);
      if (!nextBusiness) {
        setSelectedBizId(null);
        return;
      }

      setSwitchInProgress(true);
      dispatch({ type: "BUSINESS_SWITCH_START" });

      try {
        let currencies = [];
        let nextWallets = [];

        if (String(nextBusiness?.status || "").toUpperCase() === "APPROVED") {
          try {
            const walletResponse = await axios.get("/v1/wallets/accounts", {
              headers: { "x-business-id": selectedBizId },
            });
            const walletData = getWalletCurrencies(walletResponse);
            currencies = walletData.currencies;
            nextWallets = walletData.wallets;
          } catch (walletError) {
            console.warn("Unable to prefetch wallets for the selected business:", walletError);
          }
        }

        if (cancelled) return;

        const savedCurrency = localStorage.getItem(`default:${selectedBizId}`);
        const preferredCurrency = String(
          savedCurrency || nextBusiness?.currency || getBusinessDefaultCurrency(nextBusiness)
        ).toUpperCase();
        const nextCurrency = currencies.includes(preferredCurrency)
          ? preferredCurrency
          : currencies.includes(getBusinessDefaultCurrency(nextBusiness))
            ? getBusinessDefaultCurrency(nextBusiness)
            : currencies[0] || getBusinessDefaultCurrency(nextBusiness);

        await axios.put(`/v1/business/current/${selectedBizId}`);
        if (cancelled) return;

        if (business?._id && currency) {
          localStorage.setItem(`default:${business._id}`, String(currency).toUpperCase());
        }
        localStorage.setItem(`default:${selectedBizId}`, nextCurrency);
        localStorage.setItem("default", nextCurrency);

        dispatch({
          type: "BUSINESS_SWITCH_COMPLETE",
          payload: {
            business: nextBusiness,
            currency: nextCurrency,
            wallets: nextWallets,
          },
        });

        navigate("/dashboard");
        window.location.reload();
      } catch (error) {
        if (!cancelled) {
          console.error("Unable to switch business:", error);
          dispatch({ type: "BUSINESS_SWITCH_CANCEL" });
          setSwitchInProgress(false);
          setSelectedBizId(null);
        }
      }
    };

    switchBusiness();
    return () => {
      cancelled = true;
    };
  }, [business?._id, allBusinesses, currency, dispatch, navigate, selectedBizId]);

  // useEffect(() => {
  //   // Trigger notification bar display only if business details are fully fetched
  //   if (business) {
  //     fetchBusinesses("/v1/business/user", null, false);
  //     setBusinessLoaded(true);

  //     const timer = setTimeout(() => {
  //       setShowNotificationBar(true);
  //     }, 2000);

  //     return () => clearTimeout(timer);
  //   }
  // }, [business]);

  useLayoutEffect(() => {
    const storedNavState = sessionStorage.getItem("isNavOpen");
    if (storedNavState !== null) {
      setIsOpen(storedNavState === "true");
    }
  }, []);

  const notifications = sortListByDate(_notifications ?? [])
    .reverse()
    .map(({ record }) => record)
    .flat();

  const accountName = capitalizeWords(
    `${business?.name ?? `${user?.firstName ?? ""} ${user?.lastName ?? ""}`}`
  );
  const accountNameArr = accountName?.split(" ");
  const accountImg =
    accountName === capitalizeWords(business?.name ?? "")
      ? business?.businessImage?.url
      : user?.profileImage?.url;

  useEffect(() => {
    if (business?._id) {
      fetchNotifications("/v1/notification/all");
    } else {
      setNotificationsLoading(false);
    }
  }, [business?._id]);

  const toggleNav = useCallback(() => {
    setIsOpen((prev) => {
      sessionStorage.setItem("isNavOpen", String(!prev));
      return !prev;
    });
  }, []);

  const tabletView = !isOpen
    ? `lg:w-[calc(100%-108px)]  xl:w-[calc(100%-108px)]`
    : `lg:w-[calc(100%-220px)]  xl:w-[calc(100%-248px)]`;

  const tabletViewWidth = !isOpen ? `lg:w-[108px] xl:w-[108px]` : `lg:w-[220px] xl:w-[248px]`;

  const noPendingDataToSubmit = currentBusinessAccount?.informationRequestStatus === 'ACTION_REQUIRED';
  const isBusinessApproved = business?.status === "APPROVED" || currentBusinessAccount?.status === "APPROVED";
  const hasPaidOpeningFee = currentBusinessAccount?.hasPaidOpeningFee;
  const showOpeningFeeBanner =
    !isLoadingCurrentBusinessAccount &&
    !isLoadingOpeningFees &&
    Boolean(currentBusinessAccount) &&
    isBusinessApproved &&
    !hasPaidOpeningFee;

  const targetBusinessId = business?._id || currentBusinessAccount?._id;

  const walletList = Array.isArray(userWalletsAccounts) && userWalletsAccounts.length > 0
    ? userWalletsAccounts
    : Array.isArray(userWallets)
      ? userWallets
      : userWallets
        ? [userWallets]
        : [];

  const businessWalletCurrency = String(
    currentBusinessAccount?.openingFeeCurrency ||
    walletList[0]?.currency ||
    business?.currency ||
    currentBusinessAccount?.currency ||
    currency ||
    getBusinessDefaultCurrency(currentBusinessAccount || business) ||
    "NGN"
  ).toUpperCase();

  const matchedFeeItem = Array.isArray(openingFees)
    ? openingFees.find(
      (item) => String(item?.currency || "").toUpperCase() === businessWalletCurrency
    ) || openingFees[0]
    : null;

  const openingFeeCurrency = String(
    matchedFeeItem?.currency ||
    currentBusinessAccount?.openingFeeCurrency ||
    businessWalletCurrency
  ).toUpperCase();

  const openingFeeAmount =
    matchedFeeItem?.amount ??
    currentBusinessAccount?.openingFeeAmount;

  const formattedFeeAmount =
    openingFeeAmount !== undefined && openingFeeAmount !== null
      ? currencyToDisplay(openingFeeCurrency, openingFeeAmount)
      : currencyToDisplay(openingFeeCurrency, 0);

  const isCryptoWallet = ["USDC", "USDT"].includes(
    String(openingFeeCurrency || businessWalletCurrency || "").toUpperCase()
  );

  const walletObj =
    walletList.find((w) => String(w?.currency || "").toUpperCase() === openingFeeCurrency) ||
    walletList.find((w) => String(w?.currency || "").toUpperCase() === businessWalletCurrency) ||
    walletList[0];
  const walletProvider =
    walletObj?.providers?.find((p) => String(p?.currency || "").toUpperCase() === openingFeeCurrency) ||
    walletObj?.providers?.find((p) => String(p?.currency || "").toUpperCase() === businessWalletCurrency) ||
    walletObj?.providers?.[0];

  const walletBankName = walletProvider?.bankName || walletProvider?.name || "";
  const walletAccountNumber = walletProvider?.accountNumber || "";
  const walletAccountName = walletProvider?.accountName || "";
  const walletAddress =
    walletProvider?.address ||
    walletProvider?.depositAddress ||
    walletProvider?.walletAddress ||
    walletObj?.address ||
    walletObj?.depositAddress ||
    walletObj?.walletAddress ||
    walletAccountNumber ||
    "";
  const walletNetwork =
    walletProvider?.network ||
    walletProvider?.chain ||
    walletProvider?.shortName ||
    walletObj?.network ||
    walletProvider?.bankName ||
    walletProvider?.name ||
    "";
  const walletNarration = business?.name
    ? `Tradevu-${business.name.replace(/\s+/g, "")}`
    : currentBusinessAccount?.name
      ? `Tradevu-${currentBusinessAccount.name.replace(/\s+/g, "")}`
      : "";

  const handlePayOpeningFee = () => {
    const hasValidTarget = isCryptoWallet
      ? Boolean(walletAddress || walletAccountNumber)
      : Boolean(walletAccountNumber);

    if (!hasValidTarget) {
      toast.error("You don't have an active wallet yet. Please try again shortly or contact support.");
      return;
    }
    setIsOpeningFeeConfirmed(false);
    setOpeningFeeBannerStatus("approved");
    setActiveOpeningModal("payment");
  };

  const { subscribeOpeningFeeStatus } = useOpeningFeeSocket({
    businessId: targetBusinessId,
    enabled: Boolean(targetBusinessId) && isBusinessApproved && !hasPaidOpeningFee,
    onStatusUpdated: (data) => {
      if (data?.hasPaidOpeningFee || data?.status === "PAID") {
        setIsOpeningFeeConfirmed(true);
        if (activeOpeningModal === "waiting" || activeOpeningModal === "payment") {
          setActiveOpeningModal("success");
        }
        refetchCurrentBusinessAccount?.();
      } else if (data?.status === "FAILED") {
        setIsOpeningFeeConfirmed(false);
        setOpeningFeeBannerStatus("failed");
      }
    },
  });

  // 1. Prioritized full-width banners rendered at the VERY TOP of the dashboard
  const renderTopBanner = () => {
    // Priority 1: Account Opening Fee Banner
    if (showOpeningFeeBanner) {
      return (
        <AccountOpeningBanner
          status={openingFeeBannerStatus}
          feeAmount={formattedFeeAmount}
          onPay={handlePayOpeningFee}
          onRetry={handlePayOpeningFee}
          // onLearnMore={() => {
          //   console.log("Learn more clicked");
          // }}
        />
      );
    }

    // Priority 2: Request For Information (RFI)
    if (!isLoadingCurrentBusinessAccount && noPendingDataToSubmit) {
      return <AdditionalInfoBanner />;
    }

    return null;
  };

  // 2. Standard notification bars rendered below the top nav
  const renderNotificationBar = () => {
    // If a prioritized banner is already visible at the top, suppress lower-priority bars
    if (showOpeningFeeBanner || (!isLoadingCurrentBusinessAccount && noPendingDataToSubmit)) {
      return null;
    }

    const stage = business?.stage ?? null;

    let actionPath = "/onboarding/business-details";

    if (stage === 2) {
      actionPath = "/onboarding/contact-details";
    } else if (stage === 3) {
      if (business?.type === "LLC") {
        actionPath = "/onboarding/business-verification/compliance/llc";
      } else if (business?.type === "SoleProprietorship") {
        actionPath = "/onboarding/business-verification/compliance/soleproprietorship";
      }
    } else if (stage === 4) {
      actionPath = "/onboarding/business-verification/wallets";
    } else if (stage === 5) {
      actionPath = "/onboarding/business-ownership/control-person";
    } else if (stage === 6) {
      actionPath = "/onboarding/business-ownership/directors";
    } else if (stage === 7) {
      actionPath = "/onboarding/summary";
    }

    if (
      !business ||
      (Array.isArray(business) && business.length === 0) ||
      business?.status === "PENDING" || business?.status === "STARTED"
    ) {
      return (
        <NotificationBar
          message="Your account isn’t fully activated."
          actionText="Complete registration"
          icon={<OrangeLock color="white" className="w-5 h-5" />}
          className="flex justify-center bg-[#FFEFC6] text-[#475467]"
          actionPath={actionPath}
        />
      );
    }

    if (business?.status === "SUBMITTED" || business?.status === "PROCESSING" || business?.status === "UNDER REVIEW") {
      return (
        <NotificationBar
          message="Your account is under review. This usually takes between 24-48 hours."
          className="flex justify-center bg-[#EBE4FF] text-[#6F00FF]"
        />
      );
    }

    if (business?.status === "APPROVED" && !user?.isMfaEnabled) {
      return (
        <NotificationBar
          message="Your account isn’t fully secure yet."
          actionText="Set up 2FA"
          trailingText="to protect your account and transactions."
          icon={<Lock variant="Bold" color="#DB6704" className="w-5 h-5" />}
          className="flex justify-center bg-[#FFEFC6] text-[#475467]"
          actionClassName="text-[#DB6704]"
          actionPath="/auth/setup-2fa"
        />
      );
    }

    return null;
  };

  return (
    <Drawer direction={direction}>
      <>
        {isFetchingBusinesses ? null : renderTopBanner()}

        {/* Modal 1: Payment instructions */}
        <CompleteAccountOpeningModal
          isOpen={activeOpeningModal === "payment"}
          onClose={() => setActiveOpeningModal(null)}
          amount={formattedFeeAmount}
          bankName={walletBankName}
          accountNumber={walletAccountNumber}
          accountName={walletAccountName}
          narration={walletNarration}
          isCrypto={isCryptoWallet}
          network={walletNetwork}
          address={walletAddress}
          onMoneySent={() => {
            subscribeOpeningFeeStatus?.();
            setActiveOpeningModal("waiting");
          }}
        />

        {/* Modal 2: Waiting to confirm transfer (with collapsed & expanded states) */}
        <WaitingToConfirmTransferModal
          isOpen={activeOpeningModal === "waiting"}
          onClose={() => setActiveOpeningModal(null)}
          amount={formattedFeeAmount}
          bankName={walletBankName}
          accountNumber={walletAccountNumber}
          accountName={walletAccountName}
          narration={walletNarration}
          isCrypto={isCryptoWallet}
          network={walletNetwork}
          address={walletAddress}
          initialShowDetails={false}
          isConfirmed={isOpeningFeeConfirmed}
          onDone={() => {
            if (isOpeningFeeConfirmed) {
              setActiveOpeningModal("success");
            }
          }}
          onNeedHelp={() => console.log("Need help clicked")}
        />

        {/* Modal 3: Payment received success */}
        <AccountOpeningSuccessModal
          isOpen={activeOpeningModal === "success"}
          onClose={() => setActiveOpeningModal(null)}
          onDone={() => {
            setActiveOpeningModal(null);
            refetchCurrentBusinessAccount?.();
          }}
        />
        <div className="flex">
          <aside
            className={cn(
              `transition-all hidden lg:flex flex-col bg-bg-color-10 p-3 `,
              tabletViewWidth
            )}
          >
            <NewSideNav isCollapsed={isOpen} toggleNav={toggleNav} />
          </aside>
          <div
            className={cn(
              `transition-all w-full ease-linear relative h-screen overflow-x-hidden overflow-y-auto`,
              tabletView
            )}
          >
            <div className="h-[80px] sticky top-0 w-full bg-bg-color-10 z-[100] m-0 flex items-center py-3 pr-3 pl-2 md:border justify-between">
              <div className="hidden items-center xl:flex max-w-[400px] w-full">
                <SearchInput disabled="true" />
              </div>
              <div className="flex items-center xl:hidden">
                <>
                  <DrawerTrigger asChild className="flex lg:hidden">
                    <HamburgerMenu size={24} />
                  </DrawerTrigger>

                  <DrawerContent className="z-[999]">
                    <aside className="w-[248px] flex xl:hidden h-full rounded-r-md flex-col bg-light-grey p-3">
                      <NewSideNav isMobile />
                    </aside>
                  </DrawerContent>
                </>
              </div>

              <div className="flex items-center justify-end w-1/2 gap-x-3 md:gap-x-10">
                <DropdownMenu>
                  <DropdownMenuTrigger className="bg-transparent">
                    <NotificationIcon />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="p-0 bg-white border-0 z-[200] shadow-none520:rounded-lg w-screen 520:w-[450px] overflow-hidden h-[496px]">
                    <NotificationDropdown
                      notifications={notifications}
                      notificationsLoading={notificationsLoading}
                    />
                  </DropdownMenuContent>
                </DropdownMenu>
                <Link to="/account/home">
                  <Settings />
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger className="flex items-center justify-between p-2 bg-transparent cursor-pointer gap-x-2">
                    <Avatar
                      img={accountImg}
                      className={`${accountNameArr?.length > 3 && "text-[10px] "
                        } size-[42px]`}
                      fullName={accountName}
                    />
                    <div className="flex-col items-start justify-center hidden md:flex">
                      <p className="text-sm text-normal overflow-clip whitespace-nowrap text-ellipsis max-w-[130px]">
                        {accountName}
                      </p>
                      <span className="text-xs font-light text-[#6F739B]">
                        {capitalizeWords(business?.status)}
                      </span>
                    </div>
                    <div>
                      <ArrowUpDown />
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="p-[1px] bg-[#F7F8FD] z-[200] w-[290px] shadow-none 520:rounded-lg rounded-lg overflow-hidden border border-[#E7E9FB]">
                    <div className="mb-[2px] bg-white rounded-t-lg  py-2">
                      <p className="text-[#6F739B] text-sm font-light px-3 ">
                        Switch business
                      </p>
                    </div>
                    <div className="max-h-[227px] h-full bg-white scroll-smooth !border-y border-[#EBEDF9] overflow-y-auto px-3 py-2 flex flex-col gap-y-3">
                      {businessesData?.selected && (
                        <AccountInfo
                          setSelectedBizId={setSelectedBizId}
                          biz={businessesData?.selected}
                          disabled={switchInProgress}
                        />
                      )}
                      {isFetchingBusinesses ? <p className="text-[#6F739B] text-sm font-light px-3 ">
                        <div className="flex items-center justify-center gap-x-2">
                          <Spinner />
                          <span>Loading...</span>
                        </div>
                      </p> : businessesData?.businesses?.length ? businessesData?.businesses?.map((biz, index) => {
                        return (
                          <AccountInfo
                            setSelectedBizId={setSelectedBizId}
                            biz={biz}
                            disabled={switchInProgress}
                            key={index}
                          />
                        );
                      }) : null}
                    </div>
                    <div className="m-[2px] bg-white rounded-sm">
                      <Link
                        to={"/onboarding/business-details"}
                        className="flex items-center px-3 py-3 text-sm font-normal text-gray-90 gap-x-2"
                      >
                        <Add size={20} className="text-gray-90" /> Add business
                      </Link>
                    </div>
                    <div className="m-[2px] bg-white rounded-sm">
                      <Link
                        className="flex items-center px-3 py-3 text-sm font-normal text-gray-90 gap-x-2"
                        to="/account/home"
                      >
                        <Setting2 size={20} color="#6F739B" /> Manage account
                      </Link>
                      <button
                        className="flex items-center px-3 py-3 text-sm font-normal text-gray-90 gap-x-2"
                        onClick={() => LOGOUT_USER("", dispatch)}
                      >
                        <LogoutCurve size={20} color="#6F739B" /> Logout
                      </button>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            {isFetchingBusinesses ? null : renderNotificationBar()}
            <div className="bg-bg-color-10 w-11/12 md:w-[97%] mx-auto pb-2">{children}</div>
          </div>
        </div>
      </>
    </Drawer>
  );
};

export default NewDashboardLayout;

const AccountInfo = ({ setSelectedBizId, biz, disabled = false }) => {
  return (
    <div
      onClick={() => {
        if (disabled || biz?.isCurrent) return;
        setSelectedBizId && setSelectedBizId(biz._id);
      }}
      className={cn(
        "flex items-center gap-x-2",
        disabled || biz?.isCurrent ? "cursor-default" : "cursor-pointer"
      )}
      aria-disabled={disabled}
    >
      <div>
        <Avatar
          img={biz?.businessImage?.url}
          className={`${biz?.name?.toLowerCase()?.length > 3 && "text-[10px] "
            } size-[32px] block`}
          fullName={biz?.name?.toLowerCase()}
        />
      </div>
      <p className="text-sm font-normal text-gray-90">{capitalizeWords(biz?.name)}</p>
      {biz?.isCurrent ? (
        <div className="flex items-center justify-center ml-auto !bg-white p-0 rounded-full shrink-0">
          <CheckCircleIcon />
        </div>
      ) : (
        <div className="flex ml-auto border rounded-full shrink-0 size-4"></div>
      )}
    </div>
  );
};
